"use client";

import {
	FileInputWithVisual,
	type FileInputWithVisualProps,
	Input,
	Select,
	getLocaleConstants,
} from "@tosize/ui";
import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { match } from "ts-pattern";
import { TicketType } from "../../types";

interface PhoneInputProps {
	lang: string;
	label: string;
	invalidLabel: string;
}

export function PhoneInput(props: PhoneInputProps) {
	const localeConstants = getLocaleConstants(props.lang);

	if (!localeConstants.customerServicePhone) {
		return null;
	}

	return (
		<Input
			label={props.label}
			type="tel"
			name="phone"
			autoComplete="tel"
			registerOptions={{
				validate: (value?: string) => {
					const pattern = /^\+?(\d[\d\s().-]{7,})$/;

					const isInvalid =
						value !== undefined && value !== ""
							? value.includes("12345678") ||
								value.includes("87654321") ||
								value.includes("00000000") ||
								!pattern.test(value)
							: false;

					return isInvalid ? props.invalidLabel : true;
				},
			}}
		/>
	);
}

interface EmailInputProps {
	label: string;
	invalidLabel: string;
}

export function EmailInput(props: EmailInputProps) {
	return (
		<Input
			label={props.label}
			type="email"
			autoComplete="email"
			name="email"
			registerOptions={{
				pattern: {
					value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
					message: props.invalidLabel,
				},
			}}
		/>
	);
}

interface SolutionSelectProps {
	label: string;
	choiceLabel: string;
	solutions: Record<string, string>;
	children: ReactNode;
}

export function SolutionSelect(props: SolutionSelectProps) {
	const { watch } = useFormContext();
	const solution: unknown = watch("solution");

	return (
		<>
			<Select label={props.label} name="solution">
				<option disabled selected value="">
					{props.choiceLabel}
				</option>
				{Object.entries(props.solutions).map(([key, value], index) => (
					<option value={key} key={index}>
						{value}
					</option>
				))}
			</Select>

			{solution === "Anders" && props.children}
		</>
	);
}

export function ContactInputProductUrlList({ label }: { label: string }) {
	const methods = useFormContext();
	const name = "product_urls";
	const value: unknown = methods.watch(name);
	const length = Array.isArray(value) ? value.filter(Boolean).length : 0;

	return (
		<>
			{Array.from({ length: length + 1 }).map((_, index) => (
				<Input key={index} name={`${name}.${index}`} label={`${label} #${index + 1}`} />
			))}
		</>
	);
}

interface Props {
	type: ("plaatmateriaal" | "tosize_furniture" | "samples")[];
	children: ReactNode;
}

export function ShowProductTypeClient({ type, children }: Props) {
	const { watch } = useFormContext();
	const category: unknown = watch("product_type");

	if (typeof category !== "string" || !type.includes(category)) {
		return null;
	}

	return children;
}

interface FileInputWithVisualClientProps {
	woodLabel: string;
	furnitureLabel: string;
}

export function FileInputWithVisualClient({
	woodLabel,
	furnitureLabel,
	...props
}: FileInputWithVisualClientProps & FileInputWithVisualProps) {
	const { watch } = useFormContext();
	const productType: unknown = watch("product_type");
	const category: unknown = watch("type");

	const label =
		typeof category !== "string" ||
		[TicketType.QUOTATION, TicketType.INCIDENT, TicketType.CALLBACK].includes(category)
			? match(productType)
					.with("plaatmateriaal", () => woodLabel)
					.with("tosize_furniture", () => furnitureLabel)
					.otherwise(() => props.messages.label)
			: props.messages.label;

	return <FileInputWithVisual {...props} messages={{ ...props.messages, label }} />;
}
